import React from 'react';
import { Building2 } from 'lucide-react';
import { useBranchScope } from '../../context/BranchScopeContext';

interface BranchFilterSelectProps {
  /** 'header' reads and writes the global scope; 'inline' is a controlled page filter. */
  variant?: 'header' | 'inline';
  /** Controlled value ('' = all branches). Ignored by the header variant. */
  value?: string;
  onChange?: (value: string) => void;
  allLabel?: string;
  className?: string;
  /** Render even when the user only has one branch. */
  alwaysShow?: boolean;
}

export const BranchFilterSelect: React.FC<BranchFilterSelectProps> = ({
  variant = 'inline',
  value,
  onChange,
  allLabel = 'Все филиалы',
  className,
  alwaysShow = false,
}) => {
  const { branches, selectedBranchId, setSelectedBranchId, isMultiBranch } = useBranchScope();

  // A single-branch user has nothing to switch between — keep their UI unchanged.
  if (!isMultiBranch && !alwaysShow) {
    return null;
  }

  const isHeader = variant === 'header';
  const currentValue = isHeader
    ? selectedBranchId === 'all'
      ? ''
      : String(selectedBranchId)
    : value ?? '';

  const handleChange = (next: string) => {
    if (isHeader) {
      setSelectedBranchId(next === '' ? 'all' : Number(next));
      return;
    }
    onChange?.(next);
  };

  const baseClass = isHeader
    ? 'rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
    : 'w-full px-4 py-2 border rounded-lg';

  const select = (
    <select
      className={className ?? baseClass}
      value={currentValue}
      onChange={(e) => handleChange(e.target.value)}
      aria-label={allLabel}
    >
      <option value="">{allLabel}</option>
      {branches.map((branch) => (
        <option key={branch.id} value={branch.id}>
          {branch.name}
        </option>
      ))}
    </select>
  );

  if (!isHeader) {
    return select;
  }

  return (
    <div className="flex items-center gap-2">
      <Building2 className="h-4 w-4 text-gray-400" aria-hidden="true" />
      {select}
    </div>
  );
};
